import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2, Search } from "lucide-react";

import { ApiError } from "@/lib/api";
import { fetchWikiDocument, fetchWikiOAuthStatus, searchWiki, type WikiSearchResult } from "@/lib/wiki";
import { WikiDocumentMarkdown } from "@/lib/wikiDocumentMarkdown";
import { cn } from "@/lib/utils";

export function InboundIqcWikiPanel({
  sku,
  inline = false,
}: {
  sku: string | null;
  inline?: boolean;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const trimmedSku = sku?.trim() ?? "";

  useEffect(() => {
    if (trimmedSku === "") {
      setQuery("");
      setSelectedDocumentId(null);
      return;
    }
    setQuery(`${trimmedSku} IQC`);
    setSelectedDocumentId(null);
  }, [trimmedSku]);

  const trimmedQuery = query.trim();
  const statusQuery = useQuery({
    queryKey: ["wiki", "oauth-status"],
    queryFn: fetchWikiOAuthStatus,
    retry: false,
  });
  const connected = Boolean(statusQuery.data?.connected);
  const searchQuery = useQuery({
    queryKey: ["wiki", "shipping-inbound-iqc", trimmedQuery],
    queryFn: () => searchWiki(trimmedQuery, 8),
    enabled: connected && trimmedQuery.length >= 2,
    retry: false,
    staleTime: 20_000,
  });
  const results = searchQuery.data?.results ?? [];
  const resultsKey = results.map((result) => result.id).join(",");

  useEffect(() => {
    if (results.length === 0) {
      setSelectedDocumentId(null);
      return;
    }
    setSelectedDocumentId((current) => {
      if (current != null && results.some((result) => result.id === current)) {
        return current;
      }
      return results[0]?.id ?? null;
    });
  }, [resultsKey, results]);

  const documentQuery = useQuery({
    queryKey: ["wiki", "document", selectedDocumentId],
    queryFn: () => fetchWikiDocument(selectedDocumentId ?? ""),
    enabled: connected && selectedDocumentId != null && selectedDocumentId.length > 0,
    retry: false,
    staleTime: 60_000,
  });
  const selectedResult = results.find((result) => result.id === selectedDocumentId) ?? null;
  const document = documentQuery.data?.document;

  return (
    <section
      className={cn(
        "flex min-h-0 flex-1 flex-col rounded-lg border border-slate-200 bg-slate-50",
        inline ? "min-h-[16rem]" : "p-4 shadow-sm",
      )}
      aria-label="IQC instructions"
    >
      <div className={cn("shrink-0", inline ? "border-b border-slate-200 p-3" : "mb-3")}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className={cn("font-semibold text-slate-900", inline ? "text-sm" : "text-base")}>
              IQC instructions
            </h2>
            {!inline ? (
              <p className="mt-1 text-sm text-slate-500">Outline procedures and notes for the selected SKU.</p>
            ) : null}
          </div>
          {inline && selectedDocumentId != null ? (
            <Link
              to={`/wiki/docs/${encodeURIComponent(selectedDocumentId)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
            >
              Open in wiki
              <ExternalLink className="h-3 w-3" aria-hidden />
            </Link>
          ) : null}
        </div>
      </div>

      {trimmedSku === "" ? (
        <p className={cn("text-slate-500", inline ? "p-3 text-sm" : "text-sm")}>
          Select or count a line to load IQC instructions.
        </p>
      ) : (
        <div className={cn("flex min-h-0 flex-1 flex-col", inline ? "" : "")}>
          <div className={cn("shrink-0", inline ? "border-b border-slate-200 p-3" : "")}>
            <label className="block">
              <span className="sr-only">Search IQC instructions</span>
              <div className="relative">
                <Search
                  className={cn(
                    "pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400",
                  )}
                  aria-hidden
                />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search IQC, SKU, supplier…"
                  className="h-10 w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            </label>

            {!connected ? (
              <p className="mt-2 text-sm text-amber-700">
                Connect Outline from the Wiki page to search IQC procedures here.
              </p>
            ) : searchQuery.isFetching ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Searching…
              </p>
            ) : trimmedQuery.length < 2 ? (
              <p className="mt-2 text-sm text-slate-500">Type at least two characters to search.</p>
            ) : results.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">No wiki results for this search.</p>
            ) : results.length > 1 ? (
              <ul className="mt-2 flex flex-wrap gap-1.5" role="listbox" aria-label="IQC instruction pages">
                {results.map((result: WikiSearchResult) => {
                  const selected = result.id === selectedDocumentId;
                  return (
                    <li key={result.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => setSelectedDocumentId(result.id)}
                        className={cn(
                          "max-w-full rounded-md border px-2.5 py-1.5 text-left text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1",
                          selected
                            ? "border-brand-300 bg-brand-50 text-brand-900"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100",
                        )}
                      >
                        <span className="line-clamp-2">{result.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : selectedResult != null ? (
              <p className="mt-2 truncate text-xs text-slate-600">{selectedResult.title}</p>
            ) : null}
          </div>

          <div
            className={cn(
              "min-h-0 flex-1 overflow-y-auto bg-white",
              inline ? "min-h-[12rem] p-3 sm:min-h-[16rem] sm:p-4" : "mt-2 max-h-96 rounded-md border border-slate-100 p-3",
            )}
          >
            {selectedDocumentId == null ? (
              <p className="text-sm text-slate-500">Pick a search result to view instructions here.</p>
            ) : documentQuery.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading instructions…
              </p>
            ) : documentQuery.error ? (
              <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {documentQuery.error instanceof ApiError && documentQuery.error.status === 409
                  ? "Outline sign-in is required before viewing this wiki page."
                  : documentQuery.error.message}
              </p>
            ) : document != null ? (
              <article>
                {!inline ? (
                  <header className="mb-3 border-b border-slate-100 pb-2">
                    <h3 className="text-sm font-semibold text-slate-900">{document.title}</h3>
                    {document.collectionName ? (
                      <p className="mt-0.5 text-xs text-slate-500">{document.collectionName}</p>
                    ) : null}
                  </header>
                ) : null}
                <WikiDocumentMarkdown text={document.text} compact={inline} />
              </article>
            ) : (
              <p className="text-sm text-slate-500">Wiki page not found.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
